import {smoothScrollBehavior} from '@fitro/customer-ui/smoothScrollBehavior'
import {normalizePolishPhone} from './phoneCompletion'

/**
 * The one validation rule for the contact and required consents in both Guest Checkout forms
 * (classes/events and individual training). Values are validated as typed; callers trim before sending.
 */
export type GuestContactInput = {
    firstName: string
    lastName: string
    email: string
    phone: string
    /** Checked only when the class shows participants split by gender. */
    gender?: '' | 'FEMALE' | 'MALE'
    genderRequired?: boolean
    studioTermsAccepted: boolean
    fitroTermsAccepted: boolean
}

export type GuestContactField = Exclude<keyof GuestContactInput, 'genderRequired'>

/** A key under `publicEntry.guestCheckout.fieldErrors` in the landing locale. */
export type GuestContactErrorKey =
    | 'firstNameRequired'
    | 'firstNameInvalid'
    | 'lastNameRequired'
    | 'lastNameInvalid'
    | 'emailRequired'
    | 'emailInvalid'
    | 'phoneRequired'
    | 'phoneInvalid'
    | 'genderRequired'
    | 'studioTermsRequired'
    | 'fitroTermsRequired'

export type GuestContactErrors = Partial<Record<GuestContactField, GuestContactErrorKey>>

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Mirrors the backend: letters of any script, spaces, hyphens and apostrophes, at most 100.
const NAME_PATTERN = /^(?=.*\p{L})[\p{L}\p{M} '’-]{1,100}$/u

export function validateGuestContact(input: GuestContactInput): GuestContactErrors {
    const errors: GuestContactErrors = {}
    const email = input.email.trim()
    const phone = input.phone.trim()
    const firstName = input.firstName.trim()
    const lastName = input.lastName.trim()
    if (!firstName) errors.firstName = 'firstNameRequired'
    else if (!NAME_PATTERN.test(firstName)) errors.firstName = 'firstNameInvalid'
    if (!lastName) errors.lastName = 'lastNameRequired'
    else if (!NAME_PATTERN.test(lastName)) errors.lastName = 'lastNameInvalid'
    if (!email) errors.email = 'emailRequired'
    else if (!EMAIL_PATTERN.test(email)) errors.email = 'emailInvalid'
    if (!phone) errors.phone = 'phoneRequired'
    // The backend accepts only Polish (+48) numbers for guests.
    else if (normalizePolishPhone(phone) === null) errors.phone = 'phoneInvalid'
    if (input.genderRequired && !input.gender) errors.gender = 'genderRequired'
    if (!input.studioTermsAccepted) errors.studioTermsAccepted = 'studioTermsRequired'
    if (!input.fitroTermsAccepted) errors.fitroTermsAccepted = 'fitroTermsRequired'
    return errors
}

/**
 * Moves focus to the first control (in document order) whose `name` has an error and scrolls it
 * into view. Returns whether any error was found.
 */
export function focusFirstInvalidGuestField(form: HTMLFormElement, errors: GuestContactErrors): boolean {
    const first = Array.from(form.elements).find((element): element is HTMLInputElement =>
        element instanceof HTMLInputElement && element.name in errors)
    if (!first) return false
    first.focus({preventScroll: true})
    first.scrollIntoView?.({block: 'center', behavior: smoothScrollBehavior()})
    return true
}
